import {
  PaymentTypeEnum as GQLPaymentType,
  type SettlementPaymentInput as GQLInput,
  type SettlementPayment as GQLItem,
} from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as Attachment from './Attachment';

/** How a settlement payment was made. See `SettlementType.ts` for why these are literals. */
export const PaymentType = Object.freeze({
  CASH: GQLPaymentType.Cash,
  CHECK: GQLPaymentType.Check,
  CREDIT_CARD: GQLPaymentType.CreditCard,
  NONE: GQLPaymentType.None,
  WIRE_TRANSFER: GQLPaymentType.WireTransfer,
});

export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

/** A payment as the server sends it. */
export type Payment = {
  id: number;
  amount?: number | null;
  checkWireNr?: string | null;
  dateDue?: string | null;
  datePaid?: string | null;
  description?: string | null;
  paymentType?: PaymentType | null;
  attachments?: Attachment.SettlementAttachment[] | null;
};

export const fromGql = (item: GQLItem): Payment => ({
  id: item.id,
  amount: item.amount ?? null,
  checkWireNr: item.checkWireNr ?? null,
  dateDue: item.dateDue ?? null,
  datePaid: item.datePaid ?? null,
  description: item.description ?? null,
  // The column has no `none` member -- the enum maps NONE to nil, so a null read back *is* NONE.
  // Leaving it null blanks the modal's select, which offers None as a choice it could not display.
  paymentType: item.paymentType ?? PaymentType.NONE,
  attachments: Attachment.fromGqlCollection(item.attachments),
});

/** A payment line on the way back; `payee` is absent because the form has never sent it. */
export type PaymentInput = {
  id?: number | null;
  amount?: number | null;
  checkWireNr?: string | null;
  dateDue?: string | null;
  datePaid?: string | null;
  description?: string | null;
  paymentType?: PaymentType | null;
  _destroy?: boolean | null;
};

const GQL_FIELDS = [
  'id',
  '_destroy',
  'amount',
  'checkWireNr',
  'dateDue',
  'datePaid',
  'description',
  'paymentType',
] as const;

/** `Payments.vue`'s `onRemove` spreads a whole server payment in, `attachments` and all. */
export const toGqlInput = (payment: PaymentInput): GQLInput =>
  take<GQLInput, PaymentInput>(payment, ...GQL_FIELDS);
